import AXIOS from "@/axios";
import { removeEmptyProps } from "@/utils/constants";
import type { APIResponse } from "@/types";
import { LEVELS_PER_PAGE } from "../constants/class.constants";
import type {
  CatalogLevel1,
  LevelListQuery,
  LevelTableRow,
  LevelTier,
  PaginatedLevelRows,
} from "../types/level.types";
import type { LevelApi } from "./levelApi.interface";
import type {
  LevelApiItem,
  LevelListResponse,
  LevelOption,
} from "./levelApi.types";
import type { AdminClassListResponse } from "./adminClass.types";
import { mapApiItemToAdminClass } from "./adminClass.mappers";
import { assertValidLabelText } from "@/utils/zodString";
import { slugifyCatalogSegment } from "@/utils/catalogSlug";

const LEVEL_LIST_PAGE_SIZE = 100;
const MAX_LEVEL_LIST_PAGES = 50;
const MAX_CLASS_SCAN_PAGES = 50;

async function fetchLevelItemsPage(
  tier: LevelTier,
  page: number,
  perPage: number,
  sort?: { sortBy?: string; sortOrder?: "asc" | "desc" }
): Promise<LevelListResponse> {
  const response = await AXIOS.get<LevelListResponse>(
    "/class/api/v1/level/list",
    {
      params: removeEmptyProps({
        level: tier,
        page,
        per_page: perPage,
        sortBy: sort?.sortBy,
        sortOrder: sort?.sortOrder,
      }),
    }
  );
  return response.data;
}

async function fetchLevelItems(tier: LevelTier): Promise<LevelApiItem[]> {
  const items: LevelApiItem[] = [];
  let page = 1;

  while (page <= MAX_LEVEL_LIST_PAGES) {
    const response = await AXIOS.get<LevelListResponse>(
      "/class/api/v1/level/list",
      {
        params: removeEmptyProps({
          level: tier,
          page,
          per_page: LEVEL_LIST_PAGE_SIZE,
        }),
      }
    );
    const rows = response.data?.data ?? [];
    items.push(...rows);
    const totalPages = response.data?.pageInfo?.total_pages ?? 1;
    if (page >= totalPages) break;
    page += 1;
  }

  return items;
}

async function fetchAllClassLevelRefs(): Promise<
  ReturnType<typeof mapApiItemToAdminClass>["levels"][]
> {
  const levelRefs: ReturnType<typeof mapApiItemToAdminClass>["levels"][] = [];
  let page = 1;

  while (page <= MAX_CLASS_SCAN_PAGES) {
    const response = await AXIOS.get<AdminClassListResponse>(
      "/class/api/v1/list",
      {
        params: removeEmptyProps({
          page,
          per_page: LEVEL_LIST_PAGE_SIZE,
        }),
      }
    );
    for (const item of response.data?.data ?? []) {
      levelRefs.push(mapApiItemToAdminClass(item).levels);
    }
    const totalPages = response.data?.pageInfo?.total_pages ?? 1;
    if (page >= totalPages) break;
    page += 1;
  }

  return levelRefs;
}

function toTableRow(item: LevelApiItem, index: number): LevelTableRow {
  return {
    id: item.id,
    label: item.name,
    sortOrder: index + 1,
  };
}

export const levelApiHttp: LevelApi = {
  async getCatalogLevels(): Promise<CatalogLevel1[]> {
    const [level1, level2] = await Promise.all([
      fetchLevelItems(1),
      fetchLevelItems(2),
    ]);

    return level1.map((l1) => ({
      id: l1.id,
      label: l1.name,
      levels: level2.map((l2) => ({
        id: l2.id,
        label: l2.name,
        categorySlug: slugifyCatalogSegment(l2.name) || l2.id,
      })),
    }));
  },

  async getAllLevel2Options(): Promise<LevelOption[]> {
    const level2 = await fetchLevelItems(2);
    return level2.map((l) => ({ id: l.id, label: l.name }));
  },

  async getLevel3Options(): Promise<LevelOption[]> {
    const level3 = await fetchLevelItems(3);
    return level3.map((l) => ({ id: l.id, label: l.name }));
  },

  async getLevelRows(
    tier: LevelTier,
    query: LevelListQuery = {}
  ): Promise<PaginatedLevelRows> {
    const page = query.page ?? 1;
    const pageSize = query.pageSize ?? LEVELS_PER_PAGE;
    const response = await fetchLevelItemsPage(tier, page, pageSize, {
      sortBy: query.sortBy,
      sortOrder: query.sortOrder,
    });
    const items = response.data ?? [];
    const totalItems = response.pageInfo?.total_count ?? items.length;
    const totalPages = Math.max(
      1,
      response.pageInfo?.total_pages ?? Math.ceil(totalItems / pageSize)
    );
    const currentPage = Math.min(
      response.pageInfo?.current_page ?? page,
      totalPages
    );
    const startIndex = (currentPage - 1) * pageSize;

    return {
      data: items.map((item, index) => toTableRow(item, startIndex + index)),
      totalItems,
      totalPages,
      currentPage,
    };
  },

  async addLevelValue(tier: LevelTier, label: string) {
    const name = assertValidLabelText(label);
    const response = await AXIOS.post<APIResponse<LevelApiItem>>(
      "/class/api/v1/level/add",
      { name, level: tier },
      { skipGlobalErrorToast: true }
    );
    const data = response.data?.data;
    if (!data) {
      throw new Error("Add level returned no data");
    }
    return {
      id: data.id,
      label: data.name,
      sortOrder: 1,
      message: response.data?.message?.trim() ?? "",
    };
  },

  async updateLevelValue(
    tier: LevelTier,
    id: string,
    label: string,
    options?: { previousLabel?: string }
  ) {
    const name = assertValidLabelText(label, options);
    const response = await AXIOS.patch<APIResponse<LevelApiItem>>(
      `/class/api/v1/level/update/${id}`,
      { name, level: tier, is_active: true },
      { skipGlobalErrorToast: true }
    );
    const data = response.data?.data;
    if (!data) {
      throw new Error("Update level returned no data");
    }
    return {
      id: data.id,
      label: data.name,
      sortOrder: 1,
      message: response.data?.message?.trim() ?? "",
    };
  },

  async deleteLevelValue(_tier: LevelTier, id: string) {
    const response = await AXIOS.delete<APIResponse<unknown>>(
      `/class/api/v1/level/delete/${id}`,
      { skipGlobalErrorToast: true }
    );
    return {
      message: response.data?.message?.trim() ?? "",
    };
  },

  async isLevelValueInUse(tier: LevelTier, id: string, label: string) {
    try {
      const classLevels = await fetchAllClassLevelRefs();
      return classLevels.some((levels) => {
        if (tier === 1) return levels.level1Id === id;
        if (tier === 2) return levels.level2Id === id;
        return levels.level3Id === id || levels.level3Label === label;
      });
    } catch {
      // Fail closed: block delete when usage cannot be verified
      return true;
    }
  },
};
